
import { TFunction } from "i18next";

/**
 * List of available variables that can be used in email templates
 */
export const templateVariables = [
  { name: 'customer_name', description: 'Client or recipient name' },
  { name: 'invoice_number', description: 'Invoice reference number' },
  { name: 'amount', description: 'Total invoice amount with currency' },
  { name: 'due_date', description: 'Payment due date (formatted)' },
  { name: 'business_name', description: 'Your business name' },
];

/**
 * Generates default email templates based on the user's language
 * 
 * @param t - i18next translation function
 * @returns An object containing the default email templates
 */
export const generateDefaultTemplates = (t: TFunction) => {
  return {
    initial: {
      name: t("templates.default_initial_email_name", "Default Initial Email"),
      content: `<div style="background-color: #e6f3ff; padding: 20px; border-radius: 8px; font-family: Arial, sans-serif;">
        <p style="font-size: 16px; font-weight: 400; color: #34375B; margin-bottom: 16px;">${t("templates.default_greeting", "Dear")} {customer_name},</p>
        <p style="font-size: 16px; font-weight: 400; color: #34375B; margin-bottom: 16px;">${t("templates.default_initial_message", "Please find attached the invoice")} {invoice_number} ${t("templates.default_for_amount", "for")} {amount}.</p>
        <p style="font-size: 16px; font-weight: 400; color: #34375B; margin-bottom: 16px;">${t("templates.default_payment_due", "Payment is due by")} {due_date}.</p>
        <p style="font-size: 16px; font-weight: 400; color: #34375B; margin-bottom: 0;">${t("templates.default_regards", "Best regards")},<br><span style="font-weight: 500;">{business_name}</span></p>
      </div>`,
      template_type: "initial" as const,
    },
    reminder_1: {
      name: t("templates.default_first_reminder_name", "Default First Reminder"),
      content: `<div style="background-color: #e6f3ff; padding: 20px; border-radius: 8px; font-family: Arial, sans-serif;">
        <p style="font-size: 16px; font-weight: 400; color: #34375B; margin-bottom: 16px;">${t("templates.default_greeting", "Dear")} {customer_name},</p>
        <p style="font-size: 16px; font-weight: 400; color: #34375B; margin-bottom: 16px;">${t("templates.default_reminder_message", "This is a friendly reminder about invoice")} {invoice_number} ${t("templates.default_for_amount", "for")} {amount}, ${t("templates.default_which_due", "which was due on")} {due_date}.</p>
        <p style="font-size: 16px; font-weight: 400; color: #34375B; margin-bottom: 16px;">${t("templates.default_prompt_payment", "We would appreciate your prompt payment.")}</p>
        <p style="font-size: 16px; font-weight: 400; color: #34375B; margin-bottom: 0;">${t("templates.default_regards", "Best regards")},<br><span style="font-weight: 500;">{business_name}</span></p>
      </div>`,
      template_type: "reminder_1" as const,
    },
    reminder_2: {
      name: t("templates.default_second_reminder_name", "Default Second Reminder"),
      content: `<div style="background-color: #e6f3ff; padding: 20px; border-radius: 8px; font-family: Arial, sans-serif;">
        <p style="font-size: 16px; font-weight: 400; color: #34375B; margin-bottom: 16px;">${t("templates.default_greeting", "Dear")} {customer_name},</p>
        <p style="font-size: 16px; font-weight: 400; color: #34375B; margin-bottom: 16px;">${t("templates.default_second_reminder", "We have not yet received payment for invoice")} {invoice_number} ${t("templates.default_for_amount", "for")} {amount}, ${t("templates.default_which_due", "which was due on")} {due_date}.</p>
        <p style="font-size: 16px; font-weight: 400; color: #34375B; margin-bottom: 16px;">${t("templates.default_second_payment_request", "Please arrange for payment as soon as possible.")}</p>
        <p style="font-size: 16px; font-weight: 400; color: #34375B; margin-bottom: 0;">${t("templates.default_regards", "Best regards")},<br><span style="font-weight: 500;">{business_name}</span></p>
      </div>`,
      template_type: "reminder_2" as const,
    },
    reminder_4: {
      name: t("templates.default_final_reminder_name", "Default Final Reminder"),
      content: `<div style="background-color: #e6f3ff; padding: 20px; border-radius: 8px; font-family: Arial, sans-serif;">
        <p style="font-size: 16px; font-weight: 400; color: #34375B; margin-bottom: 16px;">${t("templates.default_greeting", "Dear")} {customer_name},</p>
        <p style="font-size: 16px; font-weight: 400; color: #34375B; margin-bottom: 16px;">${t("templates.default_final_reminder", "This is our final reminder regarding payment for invoice")} {invoice_number} ${t("templates.default_for_amount", "for")} {amount}, ${t("templates.default_which_due", "which was due on")} {due_date}.</p>
        <p style="font-size: 16px; font-weight: 400; color: #34375B; margin-bottom: 16px;">${t("templates.default_final_payment_notice", "Please note that if payment is not received within 7 days, we may need to take further action.")}</p>
        <p style="font-size: 16px; font-weight: 400; color: #34375B; margin-bottom: 0;">${t("templates.default_regards", "Best regards")},<br><span style="font-weight: 500;">{business_name}</span></p>
      </div>`,
      template_type: "reminder_4" as const,
    },
  };
};

/**
 * Replaces all template variables in a string with sample values for preview
 * 
 * @param content - The template content with variables
 * @returns The content with variables replaced with sample values
 */
export const previewWithSampleData = (content: string): string => {
  return content
    .replace(/{customer_name}/g, "John Smith")
    .replace(/{invoice_number}/g, "INV-2023-001")
    .replace(/{amount}/g, "€1,000.00")
    .replace(/{due_date}/g, "January 31, 2024")
    .replace(/{business_name}/g, "Happy Co.");
};

/**
 * Get help text for available template variables
 * 
 * @param t - i18next translation function
 * @returns A formatted help text string
 */
export const getTemplateVariablesHelp = (t: TFunction): string => {
  return t(
    "templates.variables_help",
    "Available variables: {customer_name}, {invoice_number}, {amount}, {due_date}, {business_name}"
  );
};
